/**
 * Адрес, который можно показать Google, — и ничего сверх него.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ СУЩЕСТВУЕТ. GA4 не спрашивает разрешения на адрес: к
 * КАЖДОМУ хиту библиотека сама прикладывает сырой `document.location` и
 * `document.referrer`. То есть по умолчанию в чужую систему уезжают
 * `/reset-password?token=…` целиком, `/s/K3nR7xQm2vB9tLpW` — нескрываемая
 * ссылка на чей-то разбор, — и `/cabinet/people/1487`, по которому вместе с
 * идентификатором посетителя восстанавливается, чью карточку он открывал.
 * Никакая настройка в интерфейсе GA этого не отменяет: единственный способ —
 * прибивать `page_location` и `page_referrer` СВОИМ значением на каждом
 * событии (`gtag.ts`), а собирает это значение здесь.
 *
 * ПРАВИЛО. Остаётся `origin` и путь, в котором динамические куски заменены
 * шаблоном. Не остаётся ничего: ни `?query`, ни `#fragment`. Запрос выкидывается
 * ЦЕЛИКОМ, а не по списку опасных имён — список пришлось бы дополнять на каждом
 * новом параметре, и однажды его бы забыли; безопасное значение потерять не
 * жалко, опасное потерять обязательно.
 */

import { SITE_ROOT_SEGMENTS } from '@/shared/site-paths'

/**
 * Сегмент, идущий ЗА этим, — динамический.
 *
 * Таблица повторяет маршруты из `app/router.tsx`, и это сознательная копия:
 * вытащить шаблоны из роутера во время выполнения нельзя (в хите нужен путь, а
 * не совпавший маршрут), а общего правила «цифры — это идентификатор» мало для
 * `username` и токена ссылки. Появился маршрут с параметром — строка сюда.
 */
const AFTER: Record<string, string> = {
  // Ссылка на опубликованную схему. Токен известен только тому, кому её
  // отправили; в аналитике ему делать нечего тем более.
  s: ':token',
  // Карточка человека и разбор ситуации в кабинете — это записи о ТРЕТЬИХ
  // лицах, которые про существование этого сайта не знают.
  people: ':id',
  situations: ':id',
  schemes: ':id',
  observations: ':id',
  groups: ':id',
  // Публичная карточка психолога. Страница открытая, но связка «этот посетитель
  // читал вот этого специалиста» — уже наблюдение о человеке, а отчёту хватает
  // числа просмотров карточек.
  'find-psychologist': ':username',
  // Её же прежний короткий адрес — `/p/<handle>`, по тем же причинам (эдж
  // переадресует его, но в хит успевает попасть). Односимвольный сегмент в
  // роли родителя работает ровно так же: таблица смотрит на предыдущий
  // сегмент, а не на его длину.
  p: ':handle',
}

/**
 * Страница аккаунта живёт на КОРНЕ — `/anna.k`, `/ru/anna.k` (с 29.09.2026 у
 * любого аккаунта, не только у психолога). Родителя у такого сегмента нет,
 * поэтому правило обратное: первый сегмент пути (после `/ru`), который не
 * принадлежит сайту, — чьё-то имя и уезжает как `:username`. Список сегментов
 * сайта один на приложение (`shared/site-paths.ts`), его полноту сторожит
 * `reserved-handles.test.ts`; забытый маршрут здесь стоит только строки в
 * отчёте, а забытое имя стоило бы наблюдения о человеке.
 */
const USERNAME = ':username'

/** Чисто числовой сегмент — идентификатор строки, как ни назови маршрут. */
const NUMERIC = /^\d+$/

/**
 * Длинная непрозрачная строка: токен, курсор, хеш — то, чего в таблице выше
 * ещё нет, потому что маршрут завели позже этой строки.
 *
 * Шестнадцать знаков и БЕЗ дефисов: столько в токене ссылки на схему, а самый
 * длинный статический кусок пути у нас — `certification` и `psychologists`, по
 * тринадцать. Дефис исключён намеренно, иначе под шаблон уехал бы
 * `find-psychologist` — семнадцать знаков и ничей не идентификатор.
 */
const OPAQUE = /^[A-Za-z0-9]{16,}$/

/** Почта в пути — не бывает у наших маршрутов, но бывает у чужого реферера. */
const LOOKS_LIKE_EMAIL = /@/

function templateSegment(segment: string, previous: string | undefined, root: boolean): string {
  const byParent = previous !== undefined ? AFTER[previous] : undefined
  if (byParent) return byParent
  if (root && !SITE_ROOT_SEGMENTS.has(segment)) return USERNAME
  if (NUMERIC.test(segment)) return ':id'
  if (OPAQUE.test(segment)) return ':id'
  if (LOOKS_LIKE_EMAIL.test(segment)) return ':id'
  return segment
}

/**
 * Путь с зашаблоненными динамическими кусками: `/cabinet/people/17` →
 * `/cabinet/people/:id`.
 *
 * Заодно это делает отчёты читаемыми: без шаблона «страница человека» в GA4
 * рассыпается на столько строк, сколько в базе людей, и не попадает в топ
 * никогда.
 */
export function templatePath(pathname: string, ownSite = true): string {
  const parts = pathname.split('/')
  // Корневой сегмент — первый значащий, а у русской витрины — второй. Правило
  // про имя на корне — про НАШИ адреса: у чужого реферера корень — его раздел.
  const rootIndex = ownSite ? (parts[1] === 'ru' ? 2 : 1) : -1
  const out: string[] = []
  for (let i = 0; i < parts.length; i++) {
    const raw = parts[i]
    if (raw === '') {
      out.push(raw)
      continue
    }
    // Предыдущий ЗНАЧАЩИЙ сегмент — исходный, не уже зашаблоненный: иначе
    // `/cabinet/people/17` искал бы родителя `:id`.
    out.push(templateSegment(raw, parts[i - 1] || undefined, i === rootIndex))
  }
  return out.join('/') || '/'
}

/**
 * Абсолютный адрес без запроса, без фрагмента и с зашаблоненным путём.
 * Пустая строка — если разобрать нечего (внешний реферер бывает и мусором).
 */
export function sanitizeUrl(url: string | null | undefined): string {
  if (!url) return ''
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return ''
  }
  // Только http(s). `javascript:` и `data:` реферером не бывают, но и пересылать
  // их куда бы то ни было незачем.
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return ''
  return parsed.origin + templatePath(parsed.pathname, isOwnSite(parsed))
}

/**
 * Свой ли это адрес: тот же origin, что у страницы (реферер внутреннего
 * перехода), или сам hub4psy.com (ссылка из письма, из превью). Чужой сайт
 * шаблонизируется только по общим правилам — цифры, токены, почта.
 */
function isOwnSite(url: URL): boolean {
  const here = typeof window !== 'undefined' ? window.location.origin : ''
  return url.origin === here || /(^|\.)hub4psy\.com$/.test(url.hostname)
}

/**
 * Адрес текущей страницы для хита. Путь берётся аргументом, а не из
 * `location`: в SPA роутер меняет адрес и рисует новый экран не одномоментно,
 * и хит, собранный из `location` в момент эффекта, иногда описывал ПРЕДЫДУЩУЮ
 * страницу.
 */
export function sanitizedLocation(pathname: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  return origin + templatePath(pathname)
}
